import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { arrayOf, func, shape, string } from 'prop-types';
import {
  STEP_TWO_LOCATIONS_SUPP_AUTH,
  STEP_TWO_PRODUCTS_SUPP_AUTH,
} from 'feature/loading-management/utils/const';
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { connect } from 'react-redux';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';
import SelectProduct, { mapDispatchToProps, mapState } from './SelectProduct.connector';

const renderTooltipCell = ({ value }) => (
  <Tooltip arrow placement="bottom" title={value || ''}>
    <span
      style={{
        display: 'inline-block',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        verticalAlign: 'bottom',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </span>
  </Tooltip>
);

const getOptionRowId = (option, index, isShowingProducts) => {
  if (option?.value !== null && option?.value !== undefined) {
    return String(option.value);
  }
  return `${isShowingProducts ? 'product' : 'location'}-row-${index}`;
};

const SelectProductStep = ({
  identifier,
  location,
  product,
  authorizationBy,
  inactiveListOptions,
  setBatchEnabledOptions,
}) => {
  const [quickFilterInput, setQuickFilterInput] = useState('');
  const [quickFilterValues, setQuickFilterValues] = useState([]);
  const isCreateSupplierAuthEnabled = EvaluateFlag(
    process.env.REACT_APP_LD_CREATE_SUPPLIER_AUTHORIZATIONS
  );
  const isShowingProducts = authorizationBy === 'location';

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      setQuickFilterValues(
        quickFilterInput
          .split(' ')
          .map(v => v.trim())
          .filter(Boolean)
      );
    }, 200);
    return () => clearTimeout(debounceTimeout);
  }, [quickFilterInput]);

  const columns = useMemo(
    () =>
      isShowingProducts
        ? [
            {
              align: 'left',
              field: 'productIdName',
              flex: 0.5,
              headerAlign: 'left',
              headerName: 'ID',
              minWidth: 64,
              renderCell: renderTooltipCell,
              resizable: true,
            },

            {
              align: 'left',
              field: 'pidxCode',
              flex: 0.5,
              headerAlign: 'left',
              headerName: 'PIDX',
              minWidth: 64,
              renderCell: renderTooltipCell,
              resizable: true,
            },
            {
              align: 'left',
              field: 'productDisplay',
              flex: 1,
              headerAlign: 'left',
              headerName: 'Name',
              minWidth: 140,
              renderCell: renderTooltipCell,
              resizable: false,
            },
          ]
        : [
            {
              align: 'left',
              field: 'locationDisplay',
              flex: 1,
              headerAlign: 'left',
              headerName: 'Locations',
              minWidth: 180,
              renderCell: renderTooltipCell,
              resizable: false,
            },
          ],
    [isShowingProducts]
  );

  const rows = useMemo(
    () =>
      inactiveListOptions.map((item, index) => {
        const rowId = getOptionRowId(item, index, isShowingProducts);
        if (isShowingProducts) {
          return {
            id: rowId,
            pidxCode: item.pidx || '--',
            productDisplay: item.label || '--',
            productIdName: item.productIdName || '--',
          };
        }
        return {
          id: rowId,
          locationDisplay: item.label || '--',
        };
      }),
    [inactiveListOptions, isShowingProducts]
  );

  const filterModel = useMemo(() => ({ items: [], quickFilterValues }), [quickFilterValues]);

  const handleSelection = useCallback(
    rowSelectionModel => {
      const selectedIds = new Set(rowSelectionModel.map(id => String(id)));
      const updates = inactiveListOptions.reduce((acc, option) => {
        const isCurrentlySelected = !!option.selected;
        const willBeSelected = selectedIds.has(String(option.value));
        if (isCurrentlySelected !== willBeSelected) {
          acc.push({ id: option.value, selected: willBeSelected });
        }
        return acc;
      }, []);

      if (updates.length > 0) {
        setBatchEnabledOptions(updates);
      }
    },
    [inactiveListOptions, setBatchEnabledOptions]
  );

  const selectedRowIds = useMemo(
    () =>
      inactiveListOptions
        .map((option, index) => ({
          rowId: getOptionRowId(option, index, isShowingProducts),
          selected: !!option.selected,
        }))
        .filter(option => option.selected)
        .map(option => option.rowId),
    [inactiveListOptions, isShowingProducts]
  );

  const handleClearSearch = useCallback(() => {
    setQuickFilterInput('');
  }, []);

  if (!isCreateSupplierAuthEnabled) {
    return <SelectProduct />;
  }

  return (
    <>
      <Divider sx={{ mt: 3 }} />
      <Typography fontWeight={700} sx={{ mb: 1, mt: 3, px: 3 }} variant="body1">
        Select {isShowingProducts ? 'Products' : 'Locations'}
      </Typography>
      <Typography
        color="text.secondary"
        fontFamily="Overpass"
        fontWeight={400}
        sx={{ mt: 0.5, px: 3 }}
        variant="body2"
      >
        {`${
          isShowingProducts ? STEP_TWO_PRODUCTS_SUPP_AUTH : STEP_TWO_LOCATIONS_SUPP_AUTH
        }${identifier} ${isShowingProducts ? 'at' : 'for'} ${
          isShowingProducts ? location : product
        }?`}
      </Typography>
      <Box display="flex" flexDirection="column" minHeight={0} pt={2} px={3} sx={{ flex: 1 }}>
        <Box pb={3}>
          <TextField
            fullWidth
            label="Search"
            onChange={e => setQuickFilterInput(e.target.value)}
            placeholder={isShowingProducts ? 'Search Product' : 'Search Location'}
            size="small"
            slotProps={{
              input: {
                endAdornment: quickFilterInput ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear search"
                      edge="end"
                      onClick={handleClearSearch}
                      size="small"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
              },

              inputLabel: { shrink: true },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& input': { padding: '14px 4px' },
              },
            }}
            value={quickFilterInput}
            variant="outlined"
          />
        </Box>
        <Box sx={{ height: 'calc(100vh - 400px)', minHeight: 0 }}>
          <DataGridPremium
            autoHeight={rows.length <= 5}
            checkboxSelection
            columns={columns}
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            filterModel={filterModel}
            hideFooterRowCount
            onRowSelectionModelChange={handleSelection}
            rows={rows}
            rowSelectionModel={selectedRowIds}
            sortingOrder={['asc', 'desc', null]}
            sx={{
              '& .MuiDataGrid-main': { overflowX: 'hidden' },
              '& .MuiDataGrid-virtualScroller': {
                overflowX: 'hidden',
                overflowY: rows.length > 0 ? 'auto' : 'hidden',
              },
            }}
          />
        </Box>
      </Box>
    </>
  );
};

SelectProductStep.propTypes = {
  authorizationBy: string.isRequired,
  identifier: string,
  inactiveListOptions: arrayOf(shape({})).isRequired,
  location: string,
  product: string,
  setBatchEnabledOptions: func.isRequired,
};

SelectProductStep.defaultProps = {
  identifier: '',
  location: '',
  product: '',
};

export default connect(mapState, mapDispatchToProps)(SelectProductStep);
